import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { dispatchEvent } from '@fil-global/shared';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-close';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-alert-circle';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-alert-diamond';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-check-circle-1';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-info-circle';

import '@fil-global/fil-link';

import componentStyles from './fil-page-level-message.styles.js';

/**
 * @tag fil-page-level-message
 * @property {"primary"|"secondary"} [appearance='primary'] Appearance of the component
 * @property {boolean} [closable] Whether the component has a close button
 * @property {boolean} [icon] Use icon in the component
 * @property {string} [linkLabel] Label of the link in the component
 * @property {string} [linkUrl] URL of the link in the component
 * @property {boolean} [show] show/hide the component
 * @property {'success' | 'info' | 'warning' | 'error'} [status='info'] Status of the component
 * @property {string} [title] Title of the component
 * @fires close.gds.page-level-message (event: Event) => void
 */
export class FilPageLevelMessage extends LitElement {
  static properties = {
    appearance: { type: String }, // 'primary' | 'secondary'
    closable: { type: Boolean },
    icon: { type: Boolean },
    linkLabel: { type: String },
    linkUrl: { type: String },
    show: { type: Boolean },
    status: { type: String }, // 'success' | 'info' | 'warning' | 'error'
    title: { type: String },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.appearance = 'primary';
    this.closable = false;
    this.icon = false;
    this.show = true;
    this.status = 'info';
  }

  onClick() {
    this.show = false;
    dispatchEvent(this, 'close.gds.page-level-message', {
      value: this.show,
    });
  }

  onKeypress(event) {
    // check for key 'space'
    if (event.keyCode === 32) {
      event.preventDefault();
      this.onClick();
    }
  }

  get iconTemplate() {
    switch (this.status) {
      case 'error':
        return html`<fil-icon-custom-alert-circle
          color="brand-error-red"
          filled
          label="Error"
        ></fil-icon-custom-alert-circle>`;
      case 'success':
        return html`<fil-icon-custom-check-circle-1
          color="green-600"
          filled
          label="Success"
        ></fil-icon-custom-check-circle-1>`;
      case 'warning':
        return html`<fil-icon-custom-alert-diamond
          color="orange-600"
          filled
          label="Warning"
        ></fil-icon-custom-alert-diamond>`;
      default:
        return html`<fil-icon-custom-info-circle
          color="blue-500"
          filled
          label="Information"
        ></fil-icon-custom-info-circle>`;
    }
  }

  render() {
    const classes = {
      [`fil-page-level-message--${this.appearance}`]: this.appearance !== 'primary',
      [`fil-page-level-message--${this.status}`]: this.status,
    };

    return html`${this.show
      ? html`<div class="fil-page-level-message${classMap(classes)}" role="alert">
          ${this.icon
            ? html`<div class="fil-page-level-message__icon">${this.iconTemplate}</div>`
            : null}
          ${this.closable && this.status !== 'error'
            ? html`<fil-icon-close
                class="fil-page-level-message__close"
                @click=${this.onClick}
                @keypress=${this.onKeypress}
                ?disabled=${this.status === 'error'}
                size="16"
                tabindex="0"
                role="button"
              ></fil-icon-close>`
            : null}
          <div class="fil-page-level-message__body">
            ${this.title
              ? html`<div class="fil-page-level-message__title">
                  <strong>${this.title}</strong>
                </div>`
              : null}
            <div class="fil-page-level-message__text"><slot></slot></div>
            ${this.linkUrl && this.linkLabel
              ? html`<div class="fil-page-level-message__link">
                  <fil-link href=${this.linkUrl} linkRole="link" aria-label="${this.linkLabel}"
                    >${this.linkLabel}</fil-link
                  >
                </div>`
              : null}
          </div>
        </div>`
      : null}`;
  }
}

export default FilPageLevelMessage;
